/* This is for when a person clicks edit from the cart so we can navigate into the modal
 * with prefilled values. this is a reverse read from the url to map to state selections
 * _id=item_40886 => {
 * userSelections: {
 *  pickerAspect_GallonFlavour: ''
 * }
 */

import { isUndefined } from 'lodash-es';
import uuidv4 from 'uuid/v4';

import { getFirstStringInLocaleBlockContent } from 'utils/sanity';

import { generateCartItemUrl } from '../routing';
import { PluTypes, PosVendors } from '../vendor-config';

import { CartEntryType } from './types';

export * from './build-cart-input';
export * from './types';

const StaticIgnoreConfig = Object.values(PosVendors).reduce(
  (acc, vendor) => ({
    ...acc,
    [vendor]: {
      pluType: PluTypes.IGNORE
    }
  }),
  {}
);

function frontendToBackendCartEntryMap({
  cartId,
  children,
  _id,
  plus,
  pickerSelections,
  image,
  uiPattern, // eslint-disable-line @typescript-eslint/no-unused-vars
  labelAsPerPerson, // eslint-disable-line @typescript-eslint/no-unused-vars
  rewardEligible, // eslint-disable-line @typescript-eslint/no-unused-vars
  vendorConfigs,
  ...rest
}) {
  const remapped = {
    lineId: cartId,
    plu: plus || {},
    sanityId: _id,
    children: children.map(frontendToBackendCartEntryMap),
    image: JSON.stringify(image),
    vendorConfigs,
    ...rest
  };

  if (pickerSelections) {
    remapped.pickerSelections = JSON.stringify(pickerSelections);
  }

  delete remapped.isUpsell;
  delete remapped.recommendationToken;
  delete remapped.recommender;

  return remapped;
}

export function remappedCartForBackEnd(cart) {
  return cart.map(frontendToBackendCartEntryMap);
}

// is this used?
export function readStateFromUrl(data, location) {
  const urlParams = new URLSearchParams(location.search);

  let selectedOption;
  let comboSlotSelections = {};
  let pickerSelections = {};
  const offerPlus = urlParams.getAll('offer').reduce((map, value) => {
    const [vendor, plu] = value.split('-');
    return {
      ...map,
      [vendor]: plu
    };
  }, {});

  const id = urlParams.get('_id');
  if (id) {
    // turn comboSlots into a map w/ quantity {item_4321: "2"}
    const comboSlots = urlParams.getAll('children').reduce((map, value) => {
      const [childId, quantity] = value.split('-');
      map[childId] = quantity;
      return map;
    }, {});

    // This is an individual item, nothing to do here.
    if (!data.options || data.options.length === 0) {
      return { cartIdEditing: urlParams.get('cartId') };
    }
    // If of type combo treat as if selections already made.
    selectedOption =
      data._type === 'combo'
        ? { option: data }
        : data.options.find(({ option }) => option._id === id);

    // If combo set the selections that the user has previously made
    if (selectedOption && selectedOption.option && selectedOption.option._type === 'combo') {
      selectedOption.option.options.forEach(comboSlot => {
        comboSlotSelections[comboSlot._id] = comboSlot.options
          // find teh selected items by id
          .filter(wrapper => Object.keys(comboSlots).includes(wrapper.option._id))
          // map those items to the shape of our menu modal
          .map(option => ({ option, quantity: Number(comboSlots[option.option._id]) }));
      });
    }

    // set previous pickerSelection if available
    (data.pickerAspects || []).forEach((pickerAspect, i) => {
      pickerSelections[pickerAspect._id] =
        selectedOption.pickerItemMappings[i].pickerAspectValueIdentifier;
    });
  }

  return {
    comboSlotSelections,
    pickerSelection: selectedOption ? selectedOption.option : {},
    pickerSelections,
    cartIdEditing: urlParams.get('cartId'),
    offerPlus
  };
}

function deriveType(item) {
  const itemType = item._type;
  if (!itemType) {
    throw new Error('_type is missing on item');
  }

  switch (itemType) {
    case 'combo':
      return CartEntryType.combo;
    case 'item':
      return CartEntryType.item;
    case 'comboSlot':
      return CartEntryType.comboSlot;
    case 'itemOption':
      return CartEntryType.itemOption;
    case 'itemOptionModifier':
      return CartEntryType.itemOptionModifier;
    case 'offer':
      return CartEntryType.offerDiscount;
    default:
      throw new Error('somehow deriving a type from a non-defined type');
  }
}

export const addCartIdToItem = item => ({
  ...item,
  cartId: uuidv4()
});

//TODO: refactor and break into more easily maintainable parts.
export function createCartEntry(object, opts = {}) {
  const type = deriveType(object);
  const {
    modifierPricingFunction = () => 0,
    curriedModifierPricingFunction = () => () => 0
  } = opts;
  const { _id, vendorConfigs, name, image, _key, labelAsPerPerson, rewardEligible } = object;
  const cartEntry = addCartIdToItem({
    _id: _id || _key,
    vendorConfigs,
    name: name?.locale,
    labelAsPerPerson,
    type,
    image,
    rewardEligible
  });

  let children = [];

  switch (type) {
    case CartEntryType.combo: {
      const { state, price } = opts;
      const comboSlots = Object.values(state.comboSlotSelections);
      if (object.mainItem) {
        // modifiers with no combo slot id correspond to the main item
        const mainItemModifiers = state.modifierSelections.filter(({ comboSlotId }) =>
          isUndefined(comboSlotId)
        );

        children.push(
          createCartEntry(object.mainItem, {
            modifierSelections: mainItemModifiers,
            curriedModifierPricingFunction
          })
        );
      }

      children = [
        ...children,
        ...comboSlots.map(slot =>
          createCartEntry(slot.data, {
            selections: slot.selections,
            curriedModifierPricingFunction,
            modifierSelections: state.modifierSelections.filter(
              ({ comboSlotId }) => comboSlotId === slot.data._id
            )
          })
        )
      ];

      if (!isNaN(price)) {
        cartEntry.price = price;
      }

      cartEntry.quantity = state.quantity || 1;
      cartEntry.url = generateCartItemUrl(object._id);
      cartEntry.pickerSelections = state.pickerSelections;

      break;
    }

    case CartEntryType.comboSlot: {
      cartEntry.quantity = 1;
      cartEntry.uiPattern = object.uiPattern;
      const { modifierSelections } = opts;

      children = [
        ...children,
        ...(opts.selections || []).map(selection =>
          createCartEntry(selection.option.option, {
            quantity: selection.quantity,
            curriedModifierPricingFunction,
            modifierSelections
          })
        )
      ];
      break;
    }

    case CartEntryType.item: {
      const { price, quantity, modifierSelections } = opts;

      cartEntry.quantity = quantity || object.quantity || 1;
      cartEntry.url = generateCartItemUrl(object._id);

      if (!isNaN(price)) {
        cartEntry.price = price;
      }

      if (modifierSelections) {
        const itemOpts = modifierSelections.filter(selection => selection.itemId === object._id);

        children = composeItemOptionModifiers(itemOpts).map(selection =>
          // relate the item to the item option modifiers
          createCartEntry(selection, {
            modifierPricingFunction: curriedModifierPricingFunction(object)
          })
        );
      }
      break;
    }

    case CartEntryType.itemOption:
      cartEntry.quantity = 1;
      cartEntry.vendorConfigs = StaticIgnoreConfig;

      children = object.options.map(modifier =>
        createCartEntry(modifier, {
          modifierPricingFunction: pricingModifier =>
            modifierPricingFunction(object, pricingModifier)
        })
      );
      break;
    case CartEntryType.itemOptionModifier:
      cartEntry.price = modifierPricingFunction(object);
      cartEntry.quantity = object.quantity || object.multiplier;
      break;
    default:
      break;

    case CartEntryType.offerDiscount:
      cartEntry.name = getFirstStringInLocaleBlockContent(object.name);
      break;
  }
  cartEntry.children = children;
  return cartEntry;
}

function composeItemOptionModifiers(modifierSelections) {
  // TODO: At some point we may have itemOptions that are duplicated for a cartEnrty, and will need to add more uniqueness to the keys
  const reducedItemOpts = modifierSelections.reduce((acc, option) => {
    if (acc[option._key]) {
      acc[option._key].options.push(option.modifier);
      return acc;
    }
    const { _key, modifier, ...rest } = option;
    return {
      ...acc,
      [_key]: {
        _key,
        ...rest,
        options: [modifier]
      }
    };
  }, {});

  return Object.values(reducedItemOpts);
}

export * from './build-cart-input';
export { buildCommitDeliveryInput } from './build-commit-delivery-input';
export { buildCommitOrderInput } from './build-commit-order-input';
export { buildPriceDeliveryInput } from './build-price-delivery-input';
export { buildPriceOrderInput } from './build-price-order-input';
export { buildStoreAddress } from './build-store-address';
